import React, { useState, useEffect, useRef } from "react";
import "./tableStyle.css";
import {
  getclientslist,
  reportsApi,
  exportCustomReportExcel,
} from "../../Services/Services";
import { Col, Input, Button, Select, Form, message } from "antd";
import { useReactToPrint } from "react-to-print";
import { FaFilePdf, FaFileExcel } from "react-icons/fa";
import InfiniteScroll from "react-infinite-scroll-component";
import { Link } from "react-router-dom";

const { Option } = Select;

export default function CustomReport({ onChange }) {
  const [orderList, setOrderList] = useState([]);
  const [deliveryOrder, setDeliveryOrder] = useState([]);
  const [form] = Form.useForm();
  const [partyList, setPartyList] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  const [isExporting, setIsExporting] = useState(false);
  const [filters, setFilters] = useState({
    party_id: "",
    from_date: "",
    to_date: "",
  });

  const limit = 50;
  const componentRef = useRef();
  const handlePrint = useReactToPrint({ content: () => componentRef.current });
  const GetClients = async () => {
    const response = await getclientslist();
    if (response) {
      const sortedArray = [...response.client_list].sort(
        (a, b) => b.client_id - a.client_id
      );
      const filterData = sortedArray.filter(
        (o) => o.com_name === localStorage.getItem("com_name")
      );
      setPartyList(filterData);
    }
  };
  useEffect(() => {
    GetClients();
  }, []);

  // Fetch data with pagination
  const fetchData = async (offset = 0, appliedFilters = filters) => {
    try {
      const response = await reportsApi({
        report_name: "custom_report",
        com_name: localStorage.getItem("com_name"),
        limit,
        offset,
        ...appliedFilters,
      });

      const newData = Array.isArray(response)
        ? response
        : response.results
        ? response.results
        : [];

      setOrderList((prev) => {
        const updated = offset === 0 ? newData : [...prev, ...newData];
        setDeliveryOrder(updated);
        return updated;
      });

      if (response && typeof response.next !== "undefined") {
        setHasMore(!!response.next);
      } else {
        setHasMore(newData.length === limit);
      }
    } catch (err) {
      console.error("CustomReport fetch error:", err);
      message.error("Failed to fetch data");
      setHasMore(false);
    }
  };

  // Initial data load
  useEffect(() => {
    setOrderList([]);
    setDeliveryOrder([]);
    setHasMore(true);
    fetchData(0);
  }, []);

  // Load more data for infinite scroll
  const fetchMoreData = () => {
    if (!hasMore) return;
    const offset = orderList.length;
    fetchData(offset);
  };

  // Handle form value changes
  const handleValuesChange = (changedValues, allValues) => {
    setFilters(allValues);
  };

  // Handle party selection
  const handlePartyChange = (value) => {
    const updatedFilters = { ...filters, party_id: value || "" };
    setFilters(updatedFilters);
    setOrderList([]);
    setDeliveryOrder([]);
    setHasMore(true);
    fetchData(0, updatedFilters);
  };

  // Handle date changes
  const handleFromDateChange = (e) => {
    setFilters((prev) => ({ ...prev, from_date: e.target.value }));
  };

  const handleToDateChange = (e) => {
    setFilters((prev) => ({ ...prev, to_date: e.target.value }));
  };

  // Handle search button click
  const handleSearch = () => {
    setOrderList([]);
    setDeliveryOrder([]);
    setHasMore(true);
    fetchData(0, filters);
  };

  // **NEW: Handle Excel Export using API**
  const handleExportToExcel = async () => {
    try {
      setIsExporting(true);
      message.loading({ content: "Generating Excel file...", key: "export" });

      // Call the API service function
      const result = await exportCustomReportExcel({
        com_name: localStorage.getItem("com_name"),
        party_id: filters.party_id,
        from_date: filters.from_date,
        to_date: filters.to_date,
      });

      if (!result.success) {
        throw new Error(result.error || "Export failed");
      }

      // Create blob and download
      const blob = new Blob([result.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;

      // Extract filename from headers or use default
      const contentDisposition = result.headers["content-disposition"];
      let filename = `Custom_Report_${
        new Date().toISOString().split("T")[0]
      }.xlsx`;

      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?(.+)"?/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1];
        }
      }

      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();

      // Cleanup
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);

      message.success({
        content: "Excel file downloaded successfully!",
        key: "export",
        duration: 3,
      });
    } catch (error) {
      console.error("Export error:", error);
      message.error({
        content: `Failed to export: ${error.message}`,
        key: "export",
        duration: 5,
      });
    } finally {
      setIsExporting(false);
    }
  };

  // Format date from YYYY-MM-DD to DD-MM-YYYY
  const formateDate = (date) => {
    if (!date) return "";
    const [year, month, day] = date.split("-");
    return `${day}-${month}-${year}`;
  };

  // Calculate totals for display
  const calculateTotal = (field) => {
    return deliveryOrder.reduce(
      (accumulator, currentValue) =>
        accumulator + parseFloat(currentValue[field] || 0),
      0
    );
  };

  return (
    <div>
      {/* Filter Form */}
      <Form form={form} layout="vertical" onValuesChange={handleValuesChange}>
        <div className="grid grid-cols-1 sm:grid-cols-5 gap-3 px-5 items-end">
          {/* Party Name Filter */}
          <Col>
            <label>
              <b>Party Name</b>
            </label>
            <Form.Item name="party_name" className="flex flex-col justify-end">
              <Select
                placeholder="Select Party"
                showSearch
                allowClear
                onChange={handlePartyChange}
                filterOption={(input, option) =>
                  option.children.toLowerCase().indexOf(input.toLowerCase()) >=
                  0
                }
              >
                {partyList.map((party) => (
                  <Option key={party.client_id} value={party.client_id}>
                    {party.client_name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>

          {/* From Date Filter */}
          <Col>
            <label>
              <b>From Date</b>
            </label>
            <Form.Item name="from_date" className="flex flex-col justify-end">
              <Input type="date" onChange={handleFromDateChange} />
            </Form.Item>
          </Col>

          {/* To Date Filter */}
          <Col>
            <label>
              <b>To Date</b>
            </label>
            <Form.Item name="to_date" className="flex flex-col justify-end">
              <Input type="date" onChange={handleToDateChange} />
            </Form.Item>
          </Col>

          {/* Action Buttons */}
          <Col className="flex items-center h-full gap-2">
            {/* Search Button */}
            <Button
              style={{
                backgroundColor: "blue",
                color: "white",
                marginRight: "30px",
              }}
              onClick={handleSearch}
            >
              Search
            </Button>

            {/* PDF Export Button */}
            <Button
              style={{
                backgroundColor: "black",
                color: "white",
                fontWeight: "600",
                marginRight: "20px",
              }}
              onClick={handlePrint}
              title="Export to PDF"
            >
              <FaFilePdf />
            </Button>

            {/* Excel Export Button */}
            <Button
              style={{
                backgroundColor: "green",
                color: "white",
                fontWeight: "600",
              }}
              onClick={handleExportToExcel}
              loading={isExporting}
              disabled={isExporting}
              title="Export to Excel"
            >
              <FaFileExcel />
              {isExporting && (
                <span style={{ marginLeft: "8px" }}>Exporting...</span>
              )}
            </Button>
          </Col>
        </div>
      </Form>

      {/* Data Table with Infinite Scroll */}
      <div
        id="scrollableDiv"
        style={{ maxHeight: "500px", overflow: "auto" }}
        ref={componentRef}
      >
        <table id="my-table" className="w-full">
          <thead className="bg-[#151B54] w-full text-white">
            <tr>
              <th>Sr. No</th>
              <th>O. Id</th>
              <th>Do No</th>
              <th>DO Qty</th>
              <th>DO Start Date</th>
              <th>DO End Date</th>
              <th>Party Name</th>
              <th>Truck Owner Name</th>
              <th>Mines Name</th>
              <th>Pan No</th>
              <th>Vehicle No</th>
              <th>Dispatch Qty</th>
              <th>Received Qty</th>
              <th>Freight Qty</th>
              <th>Sort Qty</th>
              <th>Moisture %</th>
              <th>Actual Qty</th>
              <th>Moisture Deduct Qty</th>
              <th>Moisture Amt</th>
              <th>Moisture Deduct</th>
              <th>Sort Amt</th>
              <th>Freight Rate</th>
              <th>Freight Amt</th>
              <th>Tds %</th>
              <th>Tds Amt</th>
              <th>Advance Amt</th>
              <th>Commission Amt</th>
              <th>Pay Amt</th>
              <th>Dispatch Date</th>
              <th>Received Date</th>
              <th>Check No</th>
              <th>Check Date</th>
              <th>Bank Name</th>
              <th>IFSC Code</th>
              <th>Account No</th>
              <th>Aadhar No</th>
            </tr>
          </thead>
          <tbody>
            {deliveryOrder.map((e, index) => (
              <tr key={e.id}>
                <td>{index + 1}</td>
                <td>{e.id}</td>
                <td>
                  <Link to={`/do-view/${e.do_id}`}>
                    <u style={{ fontWeight: "600", color: "blue" }}>
                      {e.do_no}
                    </u>
                  </Link>
                </td>
                <td>{e.do_qty}</td>
                <td>{e.do_start_date}</td>
                <td>{e.do_end_date}</td>
                <td>{e.client_name}</td>
                <td>{e.truck_owner_name}</td>
                <td>{e.mines_name}</td>
                <td>{e.pan_no}</td>
                <td>{e.vehicle_no}</td>
                <td>{parseFloat(e.dispatch_quantity || 0).toFixed(3)}</td>
                <td>{parseFloat(e.received_quantity || 0).toFixed(3)}</td>
                <td>{parseFloat(e.freight_quantity || 0).toFixed(3)}</td>
                <td>{e.sort_quantity}</td>
                <td>{e.moisture_received_per}</td>
                <td>{e.actual_quantity}</td>
                <td>{e.moisture_deduct_quantity}</td>
                <td>{parseFloat(e.moisture_amount || 0).toFixed(2)}</td>
                <td>{parseFloat(e.moisture_deduct || 0).toFixed(2)}</td>
                <td>{parseFloat(e.sort_amount || 0).toFixed(2)}</td>
                <td>{e.freight_rate}</td>
                <td>{parseFloat(e.freight_amount || 0).toFixed(2)}</td>
                <td>{e.tds_per}</td>
                <td>{parseFloat(e.tds || 0).toFixed(2)}</td>
                <td>{parseFloat(e.advance || 0).toFixed(2)}</td>
                <td>{parseFloat(e.commision || 0).toFixed(2)}</td>
                <td>{parseFloat(e.pay_amount || 0).toFixed(2)}</td>
                <td>{formateDate(e.date)}</td>
                <td>{formateDate(e.received_date)}</td>
                <td>{e.check_no}</td>
                <td>{formateDate(e.check_date)}</td>
                <td>{e.bank_name}</td>
                <td>{e.ifsc_code}</td>
                <td>{e.account_no}</td>
                <td>{e.aadhar_no}</td>
              </tr>
            ))}

            {/* Total Row */}
            {deliveryOrder.length > 0 && (
              <tr style={{ backgroundColor: "#e9e9e9" }}>
                <td
                  className="lg:text-[12px] text-[0.41rem] p-0 sm:px-3 px-1 lg:p-1 sm:text-base"
                  colSpan={6}
                >
                  <b>Total</b>
                </td>
                <td className="lg:text-[12px] text-[0.41rem] p-0 sm:px-3 px-1 lg:p-1 sm:text-base">
                  <b>{calculateTotal("dispatch_quantity").toFixed(3)}</b>
                </td>
                <td className="lg:text-[12px] text-[0.41rem] p-0 sm:px-3 px-1 lg:p-1 sm:text-base">
                  <b>{calculateTotal("received_quantity").toFixed(3)}</b>
                </td>
                <td className="lg:text-[12px] text-[0.41rem] p-0 sm:px-3 px-1 lg:p-1 sm:text-base">
                  <b>{calculateTotal("freight_quantity").toFixed(3)}</b>
                </td>
                <td className="lg:text-[12px] text-[0.41rem] p-0 sm:px-3 px-1 lg:p-1 sm:text-base"></td>
                <td className="lg:text-[12px] text-[0.41rem] p-0 sm:px-3 px-1 lg:p-1 sm:text-base"></td>
                <td className="lg:text-[12px] text-[0.41rem] p-0 sm:px-3 px-1 lg:p-1 sm:text-base"></td>
                <td className="lg:text-[12px] text-[0.41rem] p-0 sm:px-3 px-1 lg:p-1 sm:text-base"></td>
                <td className="lg:text-[12px] text-[0.41rem] p-0 sm:px-3 px-1 lg:p-1 sm:text-base">
                  <b>{calculateTotal("moisture_amount").toFixed(2)}</b>
                </td>
                <td className="lg:text-[12px] text-[0.41rem] p-0 sm:px-3 px-1 lg:p-1 sm:text-base">
                  <b>{calculateTotal("moisture_deduct").toFixed(2)}</b>
                </td>
                <td className="lg:text-[12px] text-[0.41rem] p-0 sm:px-3 px-1 lg:p-1 sm:text-base">
                  <b>{calculateTotal("sort_amount").toFixed(2)}</b>
                </td>
                <td className="lg:text-[12px] text-[0.41rem] p-0 sm:px-3 px-1 lg:p-1 sm:text-base"></td>
                <td className="lg:text-[12px] text-[0.41rem] p-0 sm:px-3 px-1 lg:p-1 sm:text-base">
                  <b>{calculateTotal("freight_amount").toFixed(2)}</b>
                </td>
                <td className="lg:text-[12px] text-[0.41rem] p-0 sm:px-3 px-1 lg:p-1 sm:text-base"></td>
                <td className="lg:text-[12px] text-[0.41rem] p-0 sm:px-3 px-1 lg:p-1 sm:text-base">
                  <b>{calculateTotal("tds").toFixed(2)}</b>
                </td>
                <td className="lg:text-[12px] text-[0.41rem] p-0 sm:px-3 px-1 lg:p-1 sm:text-base">
                  <b>{calculateTotal("advance").toFixed(2)}</b>
                </td>
                <td className="lg:text-[12px] text-[0.41rem] p-0 sm:px-3 px-1 lg:p-1 sm:text-base">
                  <b>{calculateTotal("commision").toFixed(2)}</b>
                </td>
                <td className="lg:text-[12px] text-[0.41rem] p-0 sm:px-3 px-1 lg:p-1 sm:text-base">
                  <b>{calculateTotal("pay_amount").toFixed(2)}</b>
                </td>
                <td
                  className="lg:text-[12px] text-[0.41rem] p-0 sm:px-3 px-1 lg:p-1 sm:text-base"
                  colSpan={8}
                ></td>
              </tr>
            )}
          </tbody>
        </table>
        <InfiniteScroll
          dataLength={deliveryOrder.length}
          next={fetchMoreData}
          hasMore={hasMore}
          scrollableTarget="scrollableDiv"
          loader={
            deliveryOrder.length > 0 && hasMore ? (
              <div style={{ textAlign: "center", padding: 10 }}>Loading...</div>
            ) : null
          }
        />
        {/* No Data Message */}
        {deliveryOrder.length === 0 && !hasMore && (
          <div style={{ textAlign: "center", padding: "20px", color: "#999" }}>
            No records found
          </div>
        )}
      </div>
    </div>
  );
}
